import React, { useEffect, useState } from "react";
import { getSupabaseClient } from "../../lib/supabase";
import { PasswordInput } from '@ui';

type Admin = {
  user_id: string;
  created_at: string;
  email?: string;
  full_name?: string;
  phone?: string;
  receives_emails: boolean;
};

export default function AdminUsersPage() {
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createFormData, setCreateFormData] = useState({
    email: "",
    password: "",
    full_name: "",
    phone: "",
  });
  const [createError, setCreateError] = useState<string | null>(null);
  const [createSuccess, setCreateSuccess] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    user_id: "",
    email: "",
    full_name: "",
    phone: "",
  });
  const [editError, setEditError] = useState<string | null>(null);
  const [editSuccess, setEditSuccess] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    loadAdmins();
  }, []);

  useEffect(() => {
    if (createSuccess) {
      const timer = setTimeout(() => setCreateSuccess(null), 6000);
      return () => clearTimeout(timer);
    }
  }, [createSuccess]);

  useEffect(() => {
    if (editSuccess) {
      const timer = setTimeout(() => setEditSuccess(null), 6000);
      return () => clearTimeout(timer);
    }
  }, [editSuccess]);

  async function loadAdmins() {
    try {
      setLoading(true);
      const supabase = getSupabaseClient();

      // Fetch admins with profile information
      const { data, error } = await supabase
        .from("admins")
        .select(`
          user_id,
          created_at,
          receives_emails,
          profiles:user_id (
            email,
            full_name,
            phone
          )
        `)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error loading admins:", error);
        return;
      }

      // Transform data to flatten profiles
      const transformedData = (data || []).map((admin: any) => ({
        user_id: admin.user_id,
        created_at: admin.created_at,
        email: admin.profiles?.email,
        full_name: admin.profiles?.full_name,
        phone: admin.profiles?.phone,
        receives_emails: admin.receives_emails ?? false,
      }));

      setAdmins(transformedData);
    } catch (err) {
      console.error("Failed to load admins:", err);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateAdmin(e: React.FormEvent) {
    e.preventDefault();
    setCreateError(null);
    setCreating(true);

    try {
      const supabase = getSupabaseClient();

      const { data, error } = await supabase.functions.invoke("create-admin-account", {
        body: {
          email: createFormData.email,
          password: createFormData.password,
          full_name: createFormData.full_name || undefined,
          phone: createFormData.phone || undefined,
        },
      });

      if (error) {
        setCreateError(error.message || "Failed to create admin account");
        setCreating(false);
        return;
      }

      if (data?.error) {
        setCreateError(data.error);
        setCreating(false);
        return;
      }

      setCreateSuccess(`Admin account created successfully for ${createFormData.email}`);
      setCreateFormData({ email: "", password: "", full_name: "", phone: "" });
      setShowCreateModal(false);
      loadAdmins();
    } catch (err) {
      console.error("Error creating admin:", err);
      setCreateError("Failed to create admin account");
    } finally {
      setCreating(false);
    }
  }

  async function toggleReceivesEmails(userId: string, currentValue: boolean) {
    try {
      const supabase = getSupabaseClient();

      const { error } = await supabase
        .from("admins")
        .update({ receives_emails: !currentValue })
        .eq("user_id", userId);

      if (error) {
        console.error("Error updating receives_emails:", error);
        alert("Failed to update email preferences");
        return;
      }

      // Update local state
      setAdmins(prevAdmins =>
        prevAdmins.map(admin =>
          admin.user_id === userId
            ? { ...admin, receives_emails: !currentValue }
            : admin
        )
      );
    } catch (err) {
      console.error("Failed to toggle receives_emails:", err);
      alert("Failed to update email preferences");
    }
  }

  function openEditModal(admin: Admin) {
    setEditFormData({
      user_id: admin.user_id,
      email: admin.email || "",
      full_name: admin.full_name || "",
      phone: admin.phone || "",
    });
    setEditError(null);
    setShowDeleteConfirm(false);
    setShowEditModal(true);
  }

  async function handleEditAdmin(e: React.FormEvent) {
    e.preventDefault();
    setEditError(null);
    setEditing(true);

    try {
      const supabase = getSupabaseClient();

      const { data, error } = await supabase.functions.invoke("update-admin-profile", {
        body: {
          user_id: editFormData.user_id,
          email: editFormData.email || undefined,
          full_name: editFormData.full_name || undefined,
          phone: editFormData.phone || undefined,
        },
      });

      if (error) {
        setEditError(error.message || "Failed to update admin profile");
        setEditing(false);
        return;
      }

      if (data?.error) {
        setEditError(data.error);
        setEditing(false);
        return;
      }

      setEditSuccess(`Admin profile updated successfully for ${editFormData.email}`);
      setShowEditModal(false);
      loadAdmins();
    } catch (err) {
      console.error("Error updating admin:", err);
      setEditError("Failed to update admin profile");
    } finally {
      setEditing(false);
    }
  }

  async function handleDeleteAdmin() {
    setEditError(null);
    setDeleting(true);

    try {
      const supabase = getSupabaseClient();

      const { data, error } = await supabase.functions.invoke("delete-admin-account", {
        body: {
          user_id: editFormData.user_id,
        },
      });

      if (error) {
        setEditError(error.message || "Failed to delete admin account");
        setDeleting(false);
        setShowDeleteConfirm(false);
        return;
      }

      if (data?.error) {
        setEditError(data.error);
        setDeleting(false);
        setShowDeleteConfirm(false);
        return;
      }

      setEditSuccess(`Admin account deleted successfully`);
      setShowEditModal(false);
      setShowDeleteConfirm(false);
      loadAdmins();
    } catch (err) {
      console.error("Error deleting admin:", err);
      setEditError("Failed to delete admin account");
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="min-h-full bg-[#FFFCEF] text-[#2C2731] p-6 space-y-6">
      {/* Header */}
      <header className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-medium text-[#2C2731] font-recoleta">
            Admin Users
          </h1>
          <p className="text-sm text-[#2C2731]/80 md:text-base">
            Manage administrator accounts
          </p>
        </div>

        <button
          type="button"
          onClick={() => {
            setShowCreateModal(true);
            setCreateError(null);
          }}
          className="inline-flex items-center gap-3 rounded-lg border border-[#2C2731] bg-[#2C2731] px-4 py-2.5 text-sm font-medium text-[#FEF6D6] shadow-md transition-colors hover:bg-[#3C3450]"
        >
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#FFFCEF]/20 text-lg font-semibold">
            +
          </span>
          Add Admin
        </button>
      </header>

      {/* Success Alert */}
      {createSuccess && (
        <div className="rounded-2xl border border-[#214D3C]/40 bg-[#E4F1E8] text-[#214D3C] px-4 py-3 text-sm flex items-start justify-between gap-4">
          <p>{createSuccess}</p>
          <button
            type="button"
            onClick={() => setCreateSuccess(null)}
            className="text-xs font-semibold uppercase tracking-wide text-[#2C2731]"
          >
            Dismiss
          </button>
        </div>
      )}

      {editSuccess && (
        <div className="rounded-2xl border border-[#214D3C]/40 bg-[#E4F1E8] text-[#214D3C] px-4 py-3 text-sm flex items-start justify-between gap-4">
          <p>{editSuccess}</p>
          <button
            type="button"
            onClick={() => setEditSuccess(null)}
            className="text-xs font-semibold uppercase tracking-wide text-[#2C2731]"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Create Admin Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-[#2C2731]/70 px-4 py-16">
          <div className="w-full max-w-xl rounded-2xl border border-[#2C2731] bg-[#FFFCEF] text-[#2C2731] shadow-xl">
            <div className="flex items-center justify-between border-b border-[#2C2731]/20 px-6 py-4">
              <h2 className="text-lg font-medium font-recoleta">Create Admin Account</h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setCreateError(null);
                }}
                className="rounded-md border border-[#2C2731] bg-[#FFFCEF] px-2 py-1 text-sm font-medium text-[#2C2731] transition-colors hover:bg-[#F2EAC3]"
                aria-label="Close modal"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateAdmin} className="space-y-5 px-6 py-5">
              {createError && (
                <div className="rounded-lg border border-[#B24E4E]/40 bg-[#FCEDEA] text-[#8C2F2F] px-4 py-3 text-sm">
                  {createError}
                </div>
              )}

              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium text-[#2C2731]/80">
                  Email *
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  value={createFormData.email}
                  onChange={(e) => setCreateFormData({ ...createFormData, email: e.target.value })}
                  className="w-full rounded-lg border border-[#2C2731] bg-[#FFFCEF] px-3 py-2 text-sm text-[#2C2731] focus:border-[#2C2731] focus:outline-none focus:ring-2 focus:ring-[#2C2731]/30"
                  placeholder="admin@example.com"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="password" className="text-sm font-medium text-[#2C2731]/80">
                  Password *
                </label>
                <PasswordInput
                  id="password"
                  required
                  minLength={6}
                  value={createFormData.password}
                  onChange={(e) => setCreateFormData({ ...createFormData, password: e.target.value })}
                  className="w-full rounded-lg border border-[#2C2731] bg-[#FFFCEF] px-3 py-2 text-sm text-[#2C2731] focus:border-[#2C2731] focus:outline-none focus:ring-2 focus:ring-[#2C2731]/30"
                  placeholder="Minimum 6 characters"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="full_name" className="text-sm font-medium text-[#2C2731]/80">
                  Full Name (optional)
                </label>
                <input
                  id="full_name"
                  type="text"
                  value={createFormData.full_name}
                  onChange={(e) => setCreateFormData({ ...createFormData, full_name: e.target.value })}
                  className="w-full rounded-lg border border-[#2C2731] bg-[#FFFCEF] px-3 py-2 text-sm text-[#2C2731] focus:border-[#2C2731] focus:outline-none focus:ring-2 focus:ring-[#2C2731]/30"
                  placeholder="John Doe"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="phone" className="text-sm font-medium text-[#2C2731]/80">
                  Phone (optional)
                </label>
                <input
                  id="phone"
                  type="tel"
                  value={createFormData.phone}
                  onChange={(e) => setCreateFormData({ ...createFormData, phone: e.target.value })}
                  className="w-full rounded-lg border border-[#2C2731] bg-[#FFFCEF] px-3 py-2 text-sm text-[#2C2731] focus:border-[#2C2731] focus:outline-none focus:ring-2 focus:ring-[#2C2731]/30"
                  placeholder="+1 (555) 123-4567"
                />
              </div>

              <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setCreateError(null);
                  }}
                  className="rounded-lg border border-[#2C2731] px-4 py-2 text-sm font-medium text-[#2C2731] transition-colors hover:bg-[#F2EAC3]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="rounded-lg border border-[#2C2731] bg-[#2C2731] px-4 py-2 text-sm font-medium text-[#FEF6D6] transition-colors hover:bg-[#3C3450] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creating ? "Creating..." : "Create Admin"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Admin Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-[#2C2731]/70 px-4 py-16">
          <div className="w-full max-w-xl rounded-2xl border border-[#2C2731] bg-[#FFFCEF] text-[#2C2731] shadow-xl">
            <div className="flex items-center justify-between border-b border-[#2C2731]/20 px-6 py-4">
              <h2 className="text-lg font-medium font-recoleta">Edit Admin Profile</h2>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditError(null);
                }}
                className="rounded-md border border-[#2C2731] bg-[#FFFCEF] px-2 py-1 text-sm font-medium text-[#2C2731] transition-colors hover:bg-[#F2EAC3]"
                aria-label="Close modal"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleEditAdmin} className="space-y-5 px-6 py-5">
              {editError && (
                <div className="rounded-lg border border-[#B24E4E]/40 bg-[#FCEDEA] text-[#8C2F2F] px-4 py-3 text-sm">
                  {editError}
                </div>
              )}

              <div className="space-y-2">
                <label htmlFor="edit_email" className="text-sm font-medium text-[#2C2731]/80">
                  Email *
                </label>
                <input
                  id="edit_email"
                  type="email"
                  required
                  value={editFormData.email}
                  onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                  className="w-full rounded-lg border border-[#2C2731] bg-[#FFFCEF] px-3 py-2 text-sm text-[#2C2731] focus:border-[#2C2731] focus:outline-none focus:ring-2 focus:ring-[#2C2731]/30"
                  placeholder="admin@example.com"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="edit_full_name" className="text-sm font-medium text-[#2C2731]/80">
                  Full Name
                </label>
                <input
                  id="edit_full_name"
                  type="text"
                  value={editFormData.full_name}
                  onChange={(e) => setEditFormData({ ...editFormData, full_name: e.target.value })}
                  className="w-full rounded-lg border border-[#2C2731] bg-[#FFFCEF] px-3 py-2 text-sm text-[#2C2731] focus:border-[#2C2731] focus:outline-none focus:ring-2 focus:ring-[#2C2731]/30"
                  placeholder="John Doe"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="edit_phone" className="text-sm font-medium text-[#2C2731]/80">
                  Phone
                </label>
                <input
                  id="edit_phone"
                  type="tel"
                  value={editFormData.phone}
                  onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                  className="w-full rounded-lg border border-[#2C2731] bg-[#FFFCEF] px-3 py-2 text-sm text-[#2C2731] focus:border-[#2C2731] focus:outline-none focus:ring-2 focus:ring-[#2C2731]/30"
                  placeholder="+1 (555) 123-4567"
                />
              </div>

              <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  {!showDeleteConfirm ? (
                    <button
                      type="button"
                      onClick={() => setShowDeleteConfirm(true)}
                      className="rounded-lg border border-[#B24E4E] px-4 py-2 text-sm font-medium text-[#B24E4E] transition-colors hover:bg-[#FCEDEA]"
                    >
                      Delete Admin
                    </button>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-[#8C2F2F]">Are you sure?</span>
                      <button
                        type="button"
                        onClick={handleDeleteAdmin}
                        disabled={deleting}
                        className="rounded-lg border border-[#B24E4E] bg-[#B24E4E] px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-[#8C2F2F] disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {deleting ? "Deleting..." : "Yes, Delete"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowDeleteConfirm(false)}
                        disabled={deleting}
                        className="rounded-lg border border-[#2C2731] px-3 py-1.5 text-sm font-medium text-[#2C2731] transition-colors hover:bg-[#F2EAC3] disabled:opacity-50"
                      >
                        No
                      </button>
                    </div>
                  )}
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false);
                      setEditError(null);
                      setShowDeleteConfirm(false);
                    }}
                    className="rounded-lg border border-[#2C2731] px-4 py-2 text-sm font-medium text-[#2C2731] transition-colors hover:bg-[#F2EAC3]"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={editing}
                    className="rounded-lg border border-[#2C2731] bg-[#2C2731] px-4 py-2 text-sm font-medium text-[#FEF6D6] transition-colors hover:bg-[#3C3450] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {editing ? "Updating..." : "Update Admin"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Admin List Table */}
      <div className="overflow-hidden rounded-2xl border border-[#2C2731] bg-[#FFFCEF] shadow">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-[#2C2731] text-[#FEF6D6]">
            <tr>
              <th className="px-4 py-3 font-medium uppercase tracking-wide">
                Full Name
              </th>
              <th className="px-4 py-3 font-medium uppercase tracking-wide">
                Email
              </th>
              <th className="px-4 py-3 font-medium uppercase tracking-wide">
                Phone
              </th>
              <th className="px-4 py-3 font-medium uppercase tracking-wide">
                Receives Notifications
              </th>
              <th className="px-4 py-3 font-medium uppercase tracking-wide">
                Created At
              </th>
              <th className="px-4 py-3 font-medium uppercase tracking-wide">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#2C2731]/20">
            {loading && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-sm text-[#2C2731]/70">
                  Loading...
                </td>
              </tr>
            )}

            {!loading && admins.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-sm text-[#2C2731]/70">
                  No admin users found
                </td>
              </tr>
            )}

            {!loading &&
              admins.map((admin) => (
                <tr key={admin.user_id} className="bg-[#FFFCEF] text-[#2C2731]">
                  <td className="px-4 py-4">
                    {admin.full_name || <span className="text-[#2C2731]/50">—</span>}
                  </td>
                  <td className="px-4 py-4">
                    {admin.email || <span className="text-[#2C2731]/50">—</span>}
                  </td>
                  <td className="px-4 py-4">
                    {admin.phone || <span className="text-[#2C2731]/50">—</span>}
                  </td>
                  <td className="px-4 py-4">
                    <button
                      onClick={() => toggleReceivesEmails(admin.user_id, admin.receives_emails)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#2C2731] focus:ring-offset-2 ${
                        admin.receives_emails ? 'bg-[#2C2731]' : 'bg-gray-300'
                      }`}
                      role="switch"
                      aria-checked={admin.receives_emails}
                      aria-label={`Toggle email notifications for ${admin.email || admin.full_name || 'admin'}`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          admin.receives_emails ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </td>
                  <td className="px-4 py-4">
                    {new Date(admin.created_at).toLocaleDateString()} at{" "}
                    {new Date(admin.created_at).toLocaleTimeString()}
                  </td>
                  <td className="px-4 py-4">
                    <button
                      onClick={() => openEditModal(admin)}
                      className="rounded-lg border border-[#2C2731] bg-[#FFFCEF] px-3 py-1.5 text-xs font-medium text-[#2C2731] transition-colors hover:bg-[#F2EAC3]"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Summary */}
      {!loading && (
        <div className="text-sm text-[#2C2731]/80">
          Total admins: {admins.length}
        </div>
      )}
    </div>
  );
}
